import React, { useRef, useState } from 'react';
import { Check, Copy, Download } from 'lucide-react';
import { AppChrome } from '../components/alt/AppChrome';
import { ChatsScreen, SharedScreen, WorkflowsScreen, PulsesScreen, SkillsScreen } from '../components/alt/PlatformPillar';
import { ConnectionsScreen } from '../components/alt/ConnectedAppsPillar';
import { BrainFlowExperiment } from '../components/alt/BrainFlowExperiment';
import { ArchitectureWidget } from '../components/alt/ArchitectureWidget';
import { MOSAIC_CARDS } from '../components/alt/AccelerationMosaic';
import { ConnectedAppsTable } from './Alt';
import { SlackPanel } from '../components/SlackIntegrationGraph';
import { CodeTerminal } from './CompanyBrain';
import { ChatGPTPanel } from '../components/alt/ChatGPTPanel';
import { SurfacePill, SURFACE_PILLS, TOOL_PILLS, CRM_PILLS, type PillAsset } from '../components/alt/SurfacePill';
import { domToSvg, preloadImages, svgToPng } from '../lib/domToSvg';

/**
 * Export surface for the product screens used in the Workspace section of the
 * home page.
 *
 * These are vectorised, not screenshotted: the DOM is walked and re-emitted as
 * SVG primitives, so what lands in Figma is real rectangles, real lines and
 * real text layers you can retype and restyle — not a flat picture.
 */

type Screen = {
  id: string;
  name: string;
  note: string;
  section: string;
  /** Product screens sit in app chrome; diagrams and panels stand alone. */
  chrome?: boolean;
  /** Screen already carries its own inner padding. */
  selfPadded?: boolean;
  active?: string;
  /** Screens that are the whole view — no app nav beside them. */
  noSidebar?: boolean;
  /** Wider canvas for the full-bleed diagrams. */
  wide?: boolean;
  /**
   * Artboard width in px, for frames that were composed narrower than the
   * default 900. A card designed as a third of a twelve-column board stretches
   * badly at full width — its lists go from two lines to one and the visual
   * stops matching what ships on the page.
   */
  maxWidth?: number;
  render: () => React.ReactNode;
};
/**
 * The column width each mosaic card occupies on the home page's twelve-column
 * board, so the exported artboard matches what ships rather than a stretched
 * version of it.
 */
const MOSAIC_WIDTHS: Record<string, number> = {
  'mosaic-offload': 740,
  'mosaic-meetings': 520,
  'mosaic-answers': 420,
  'mosaic-own-data': 420,
  'mosaic-activity': 420,
  'mosaic-teams': 520,
  'mosaic-model': 740
};
const SCREENS: Screen[] = [
// ── Connected Apps ───────────────────────────────────────────────────────
{
  id: 'connections',
  name: 'Connections',
  note: 'Tools, accounts and the AI surfaces that reach them',
  section: 'Connected Apps',
  chrome: true,
  selfPadded: true,
  noSidebar: true,
  wide: true,
  render: () => <ConnectionsScreen data-id="element-9334" data-mp="14godegisba3h" />
}, {
  id: 'comparison',
  name: 'Connection comparison',
  note: 'Giga vs ChatGPT vs Claude',
  section: 'Connected Apps',
  render: () => <ConnectedAppsTable data-id="element-9335" data-mp="19do85va6oqfa" />
},
// ── Company Brain ────────────────────────────────────────────────────────
{
  id: 'brain-flow',
  name: 'Sources into tracks',
  note: 'Observations flowing from scans and chats',
  section: 'Company Brain',
  wide: true,
  render: () => <BrainFlowExperiment settled data-id="element-9336" data-mp="1avv6g213yfqr" />
},
// ── AI Co Worker ─────────────────────────────────────────────────────────
{
  id: 'slack',
  name: 'Slack thread',
  note: 'Giga answering in #sales',
  section: 'AI Co Worker',
  render: () => <SlackPanel className="w-full" heightClass="h-[560px]" platform="slack" showPlatformToggle={false} settled data-id="element-9337" data-mp="i666zt41qnr9" />
}, {
  // Teams is its own frame rather than a toggle inside the Slack one, so it
  // can be exported and reviewed on its own terms.
  id: 'teams',
  name: 'Teams chat',
  note: 'Giga answering in Microsoft Teams',
  section: 'AI Co Worker',
  render: () => <SlackPanel className="w-full" heightClass="h-[560px]" platform="teams" showPlatformToggle={false} settled data-id="element-9338" data-mp="3vlueebi5kotd" />
}, {
  id: 'claude-code',
  name: 'Claude Code terminal',
  note: 'Giga over MCP',
  section: 'AI Co Worker',
  render: () => <CodeTerminal data-id="element-9339" data-mp="2tv49powo6msf" />
}, {
  id: 'chatgpt',
  name: 'ChatGPT surface',
  note: 'Giga answering inside ChatGPT',
  section: 'AI Co Worker',
  render: () => <ChatGPTPanel heightClass="h-[560px]" settled data-id="element-9340" data-mp="32wki5r3xceu0" />
},
// ── Workspace ────────────────────────────────────────────────────────────
{
  id: 'chats',
  name: 'AI chats',
  note: 'Renewal risk in Salesforce',
  section: 'Workspace',
  chrome: true,
  noSidebar: true,
  selfPadded: true,
  render: () => <ChatsScreen data-id="element-9341" data-mp="3hlgqzqdt1vvo" />
}, {
  id: 'groups',
  name: 'Team workspaces',
  note: 'Groups',
  section: 'Workspace',
  chrome: true,
  noSidebar: true,
  render: () => <SharedScreen data-id="element-9342" data-mp="3qn7tkmd1tmlb" />
}, {
  id: 'workflows',
  name: 'Workflows',
  note: 'Monday morning update',
  section: 'Workspace',
  chrome: true,
  noSidebar: true,
  render: () => <WorkflowsScreen data-id="element-9343" data-mp="1s9eldmka6pet" />
}, {
  id: 'pulses',
  name: 'Pulses',
  note: '4 enabled',
  section: 'Workspace',
  chrome: true,
  noSidebar: true,
  render: () => <PulsesScreen data-id="element-9344" data-mp="1oq7gea8w9y8q" />
}, {
  id: 'skills',
  name: 'Skills',
  note: 'Recorded once, reused everywhere',
  section: 'Workspace',
  chrome: true,
  noSidebar: true,
  render: () => <SkillsScreen data-id="element-9345" data-mp="1ezukxmzjkknt" />
},
// ── Architecture ─────────────────────────────────────────────────────────
{
  id: 'architecture',
  name: 'Architecture',
  note: 'Ways to use Giga',
  section: 'Architecture',
  wide: true,
  render: () => <ArchitectureWidget data-id="element-9346" data-mp="27os1nlo3u9eg" />
},
// ── Speed & control board ────────────────────────────────────────────────
// The seven panels from "The fastest version of your company is already
// possible", each on its own artboard. They are pulled from the section's own
// exported registry rather than rebuilt here, so a change to a panel on the
// home page lands in the export automatically. Narrow artboards: these are
// cards, not full screens, and exporting them at 900px would stretch a
// four-column layout that was composed at roughly half that.
...MOSAIC_CARDS.map(card => ({
  id: card.id,
  name: card.name,
  note: card.note,
  section: 'Speed & control cards',
  // Each artboard is the card's own column width on the board plus the frame's
  // 40px of padding on each side.
  maxWidth: (MOSAIC_WIDTHS[card.id] ?? 560) + 80,
  render: card.render
}))];
/**
 * Clipboard write with a fallback.
 *
 * The async Clipboard API refuses to write unless the document is focused, and
 * throws "Document is not focused" if focus moved while the markup was being
 * built — clicking into another pane, or the browser blurring the preview
 * frame. The legacy execCommand path has no such requirement, so it covers the
 * gap: a hidden textarea is focused, selected and copied from directly.
 */
async function writeToClipboard(text: string) {
  try {
    if (document.hasFocus()) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the legacy path rather than surfacing a focus error.
  }
  const field = document.createElement('textarea');
  field.value = text;
  field.setAttribute('readonly', '');
  field.style.position = 'fixed';
  field.style.top = '0';
  field.style.opacity = '0';
  document.body.appendChild(field);
  field.focus();
  field.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  field.remove();
  return ok;
}
function download(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
function downloadSvg(markup: string, filename: string) {
  download(new Blob([markup], {
    type: 'image/svg+xml'
  }), filename);
}
export function ExportDashboards() {
  const frames = useRef<Record<string, HTMLDivElement | null>>({});
  const [copied, setCopied] = useState<string | null>(null);
  // Capturing is now asynchronous — every logo in the frame is fetched and
  // embedded before the markup is handed over — so the button has to say so.
  const [busy, setBusy] = useState<string | null>(null);
  const capture = async (id: string) => {
    const node = frames.current[id];
    // No artboard fill: the screens already carry their own white panel, so a
    // second one behind them only shows up as a white rectangle you have to
    // crop out. Transparent means the frame drops straight onto any surface.
    return node ? await domToSvg(node, null) : null;
  };
  // Fetch the frame's logos as soon as the pointer reaches its controls, so by
  // the time Copy is pressed the markup is built from cache and the clipboard
  // write happens while the document is still focused.
  const warm = (id: string) => {
    const node = frames.current[id];
    if (node) void preloadImages(node);
  };
  const copy = async (id: string) => {
    setBusy(id);
    try {
      const markup = await capture(id);
      if (!markup) return;
      const ok = await writeToClipboard(markup);
      // If both clipboard paths are unavailable, hand over the file instead of
      // failing silently — the user still gets something to paste from.
      if (!ok) downloadSvg(markup, `giga-${id}.svg`);
      setCopied(id);
      setTimeout(() => setCopied(c => c === id ? null : c), 2000);
    } finally {
      setBusy(b => b === id ? null : b);
    }
  };
  const save = async (id: string) => {
    setBusy(id);
    try {
      const markup = await capture(id);
      if (markup) downloadSvg(markup, `giga-${id}.svg`);
    } finally {
      setBusy(b => b === id ? null : b);
    }
  };
  // 2× rather than the lockups' 4×: these frames are already 900–1360px wide,
  // so 2× lands around 2700px — retina-sharp in a deck without producing a
  // 20MB file the browser struggles to encode.
  const savePng = async (id: string) => {
    setBusy(id);
    try {
      const markup = await capture(id);
      if (markup) download(await svgToPng(markup, 2), `giga-${id}.png`);
    } finally {
      setBusy(b => b === id ? null : b);
    }
  };
  const saveAllPng = async () => {
    for (const s of SCREENS) {
      setBusy(s.id);
      try {
        const markup = await capture(s.id);
        if (markup) download(await svgToPng(markup, 2), `giga-${s.id}.png`);
      } catch {
        // One frame failing should not stop the rest of the batch.
      }
    }
    setBusy(null);
  };
  return <main className="min-h-screen w-full bg-[#F4F1EA] pb-24" data-id="element-9347" data-mp="ukkklhzn4lw">
      <header className="border-b border-[#E7E0D4] bg-[#FAF8F4]" data-id="element-9348" data-mp="399cd5va7fz3t">
        <div className="mx-auto max-w-[1240px] px-6 py-10" data-id="element-9349" data-mp="349gaq0h5ofqd">
          <p className="text-[12px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-9350" data-mp="34b13bkjwtoll">
            Export
          </p>
          <h1 className="mt-3 font-title text-3xl font-semibold tracking-tight text-[#2B211A] md:text-[40px]" data-id="element-9351" data-mp="3e7dwmhbjm0ft">
            Home page screens
          </h1>
          <p className="mt-3 max-w-[64ch] text-[15.5px] leading-relaxed text-[#6B5D52]" data-id="element-9352" data-mp="yex5slio5cwa">
            Every product screen and diagram shown on the home page, across all
            four pillars, vectorised into editable layers. Copy one, paste into
            Figma, and you get real rectangles and real text you can retype —
            not a screenshot.
          </p>

          <ol className="mt-6 max-w-[64ch] space-y-2 rounded-xl border border-[#E7E0D4] bg-white px-5 py-4 text-[13px] leading-relaxed text-[#6B5D52]" data-id="element-9353" data-mp="s0ricoyle1jc">
            <li data-id="element-9354" data-mp="w3nvi05d55fw">
              <strong className="font-semibold text-[#2B211A]" data-id="element-9355" data-mp="1jr5p8186udli">1.</strong> Keep
              this window at desktop width — the app sidebar only renders above
              1024px, and the export captures what is actually on screen.
            </li>
            <li data-id="element-9356" data-mp="1ay2iscvyn10d">
              <strong className="font-semibold text-[#2B211A]" data-id="element-9357" data-mp="1aylo8u4fbwxi">2.</strong> Hit
              Copy, then paste directly onto a Figma canvas.
            </li>
            <li data-id="element-9358" data-mp="22pi4m4pp0jpj">
              <strong className="font-semibold text-[#2B211A]" data-id="element-9359" data-mp="22oyrd1azmjhu">3.</strong>{' '}
              Install DM Sans and Inter in Figma so the type matches. Everything
              arrives as live text layers in those families.
            </li>
          </ol>

          <button type="button" onClick={saveAllPng} disabled={busy !== null} className="mt-6 inline-flex items-center gap-2 rounded-lg bg-[#EA580C] px-4 py-2.5 text-[13px] font-bold text-white transition-colors hover:bg-[#C2410C] disabled:opacity-60" data-id="element-9360" data-mp="2db59y44vxb0t">
            <Download size={14} data-id="element-9361" data-mp="23c5yyewnd3an" />
            {busy ? 'Exporting…' : `Download all ${SCREENS.length} as PNG`}
          </button>

          <p className="mt-4 max-w-[64ch] text-[12.5px] leading-relaxed text-neutral-500" data-id="element-9362" data-mp="1yfz35exyt86f">
            Brand logos are downloaded and embedded into the file, so they
            survive the paste — that is what the short pause after pressing Copy
            is. Drop shadows are dropped rather than faked. The animated screens
            are held on their finished state here — the full Slack thread, the
            complete ChatGPT transcript, every observation landed — so the file
            is never captured mid-animation.
          </p>
        </div>
      </header>

      <div className="mx-auto max-w-[1360px] space-y-14 px-6 pt-14" data-id="element-9363" data-mp="10jtpwqst4cet">
        <SurfacePills heading="Surface lockups" title="Platform mark and name" note="Transparent artboard — the capsule is translucent, so whatever sits behind it still shows through. PNG is rasterised at 4× with the real type embedded; SVG keeps the word as live text." items={SURFACE_PILLS} data-id="element-9364" data-mp="15in5cornmdhm" />
        <SurfacePills heading="Tool lockups" title="Connected tool mark and name" note="The same lockup for every tool named across the site. Marks come from the shared brand registry, so they match the connections map exactly." items={TOOL_PILLS} data-id="element-9365" data-mp="hudkpq66fvs2" />
        <SurfacePills heading="CRM lockups" title="CRM mark and name" note="The CRMs a buyer is most likely to already run. Useful for integration pages and campaign creative that names the CRM the reader uses." items={CRM_PILLS} data-id="element-9366" data-mp="rqqcgii5vzk6" />
      </div>

      <div className="mx-auto max-w-[1360px] space-y-14 px-6 pt-14" data-id="element-9367" data-mp="41xekghzbhmx">
        {SCREENS.map((s, i) => {
        const copiedNow = copied === s.id;
        const busyNow = busy === s.id;
        const newSection = i === 0 || SCREENS[i - 1].section !== s.section;
        return <section key={s.id} data-id="element-9368" data-mp="20qp0jln2j3nk" data-mpi={i}>
              {/* Grouped by the pillar the screen belongs to, so the page
               matches the order you scroll past on the home page. */}
              {newSection && <p className="mb-5 border-b border-[#E7E0D4] pb-2 text-[11.5px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-9369" data-mp="2aomsaj44zikx">
                  {s.section}
                </p>}
              <div className="mb-4 flex flex-wrap items-end justify-between gap-4" data-id="element-9370" data-mp="2kg9a4yazcfwn">
                <div data-id="element-9371" data-mp="2yfs3um31nopf">
                  <h2 className="font-title text-[22px] font-semibold tracking-tight text-[#2B211A]" data-id="element-9372" data-mp="2ye7jtqecvo5t">
                    {s.name}
                  </h2>
                  <p className="mt-0.5 text-[12.5px] text-neutral-500" data-id="element-9373" data-mp="141hi4mhqxanv">
                    {s.note} · giga-{s.id}.svg
                  </p>
                </div>
                <div className="relative z-10 flex flex-wrap items-center gap-2" onPointerEnter={() => warm(s.id)} onFocus={() => warm(s.id)} data-id="element-9374" data-mp="3aqje73dkpnzg">
                  <button type="button" onClick={() => savePng(s.id)} disabled={busyNow} className="inline-flex items-center gap-1.5 rounded-lg bg-[#2B211A] px-3.5 py-2 text-[12.5px] font-bold text-white transition-colors hover:bg-black disabled:opacity-60" data-id="element-9375" data-mp="3fokxnpewvpbg">
                    <Download size={13} data-id="element-9376" data-mp="2s01s3hn8xqn2" />
                    {busyNow ? 'Preparing…' : 'Download PNG'}
                  </button>
                  <button type="button" onClick={() => copy(s.id)} disabled={busyNow} className="inline-flex items-center gap-1.5 rounded-lg border border-[#D8CFC2] bg-white px-3 py-2 text-[12.5px] font-semibold text-[#2B211A] transition-colors hover:border-[#2B211A] disabled:opacity-60" data-id="element-9377" data-mp="2wzekehwvzpx1">
                    {copiedNow ? <Check size={13} className="text-emerald-600" data-id="element-9378" data-mp="gbkarkh8j1ne" /> : <Copy size={13} data-id="element-9379" data-mp="g8oc1txkc8xw" />}
                    {copiedNow ? 'Copied' : 'Copy for Figma'}
                  </button>
                  <button type="button" onClick={() => save(s.id)} disabled={busyNow} className="inline-flex items-center gap-1.5 rounded-lg border border-[#D8CFC2] bg-white px-3 py-2 text-[12.5px] font-semibold text-[#2B211A] transition-colors hover:border-[#2B211A] disabled:opacity-60" data-id="element-9380" data-mp="jsy75byg6yiu">
                    <Download size={13} data-id="element-9381" data-mp="5sx7fztx5x4y" />
                    SVG
                  </button>
                </div>
              </div>

              {/* The captured node. Padding sits inside it so the exported
               artboard carries its own margin around the frame.
                `relative isolate` is load-bearing: these screens come from the
               home page, where they sit inside positioned sections. Rendered
               bare here, any decorative `absolute inset-0` layer inside them
               would resolve against the initial containing block — painting a
               viewport-sized panel over the top of the page and swallowing
               clicks on the export buttons. Positioning each frame keeps every
               overlay inside the screen it belongs to. */}
              <div ref={el => {
            frames.current[s.id] = el;
          }} style={s.maxWidth ? {
            maxWidth: s.maxWidth
          } : undefined} className={`relative isolate overflow-x-auto rounded-2xl p-10 ${s.wide ? '' : 'mx-auto max-w-[900px]'}`} data-id="element-9382" data-mp="3440gjta2vcae">
                {s.chrome ? <AppChrome active={s.active} sidebar={!s.noSidebar} data-id="element-9383" data-mp="3i4b7sxfausw3">
                    {s.selfPadded ? s.render() : <div className="px-5 py-5 md:px-7 md:py-6" data-id="element-9384" data-mp="3n3eeemaxh766">
                        {s.render()}
                      </div>}
                  </AppChrome> : s.render()}
              </div>
            </section>;
      })}
      </div>
    </main>;
}

/**
 * The surface lockups, exported on nothing.
 *
 * On the page these sit inside a segmented control, so the mark and word carry
 * a cream track behind them and an ink fill when selected. Neither is part of
 * the lockup, so both are dropped here: the artboard is genuinely transparent,
 * and the preview sits on a checkerboard to prove it.
 */
function SurfacePills({
  heading,
  title,
  note,
  items
}: {
  heading: string;
  title: string;
  note: string;
  items: PillAsset[];
}) {
  const frames = useRef<Record<string, HTMLSpanElement | null>>({});
  const [tone, setTone] = useState<'ink' | 'white'>('ink');
  const [shape, setShape] = useState<'pill' | 'bare'>('pill');
  const [busy, setBusy] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const key = (id: string) => `${id}-${tone}-${shape}`;
  const markup = async (id: string) => {
    const node = frames.current[key(id)];
    return node ? await domToSvg(node, null) : null;
  };
  const run = async (id: string, job: (svg: string) => void | Promise<void>) => {
    setBusy(key(id));
    try {
      const svg = await markup(id);
      if (svg) await job(svg);
    } finally {
      setBusy(b => b === key(id) ? null : b);
    }
  };
  return <section data-id="element-9385" data-mp="206zp0wz1rcfo">
      <p className="mb-5 border-b border-[#E7E0D4] pb-2 text-[11.5px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-9386" data-mp="2066edkwyw2s9">
        {heading}
      </p>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4" data-id="element-9387" data-mp="24zj27stto5jy">
        <div className="max-w-[62ch]" data-id="element-9388" data-mp="1q9v38y945ize">
          <h2 className="font-title text-[22px] font-semibold tracking-tight text-[#2B211A]" data-id="element-9389" data-mp="1v7mr9h6n0kyb">
            {title}
          </h2>
          <p className="mt-1 text-[13px] leading-relaxed text-neutral-500" data-id="element-9390" data-mp="2iwxwbav0t1i9">
            {note}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-id="element-9391" data-mp="2nuzfrwwcz2u9">
          <div className="inline-flex items-center gap-1 rounded-full border border-[#E7E0D4] bg-white p-1" data-id="element-9392" data-mp="28z9yud615fwe">
            {(['pill', 'bare'] as const).map(s => <button key={s} type="button" onClick={() => setShape(s)} aria-pressed={shape === s} className={`rounded-full px-3 py-1.5 text-[12.5px] font-semibold transition-colors ${shape === s ? 'bg-[#2B211A] text-white' : 'text-[#6B5D52] hover:text-[#2B211A]'}`} data-id="element-9393" data-mp="2dwjnvrpibpjp">
                {s === 'pill' ? 'Capsule' : 'Bare'}
              </button>)}
          </div>
          <div className="inline-flex items-center gap-1 rounded-full border border-[#E7E0D4] bg-white p-1" data-id="element-9394" data-mp="7ducupcs5mey">
            {(['ink', 'white'] as const).map(t => <button key={t} type="button" onClick={() => setTone(t)} aria-pressed={tone === t} className={`rounded-full px-3 py-1.5 text-[12.5px] font-semibold transition-colors ${tone === t ? 'bg-[#2B211A] text-white' : 'text-[#6B5D52] hover:text-[#2B211A]'}`} data-id="element-9395" data-mp="3pinuou9365dd">
                {t === 'ink' ? 'For light' : 'For dark'}
              </button>)}
          </div>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-9396" data-mp="36tr1yutc7kx9">
        {items.map(p => {
        const busyNow = busy === key(p.id);
        const copiedNow = copied === key(p.id);
        return <div key={p.id} className="rounded-2xl border border-[#E7E0D4] bg-white p-4" onPointerEnter={() => {
          const node = frames.current[key(p.id)];
          if (node) void preloadImages(node);
        }} data-id="element-9397" data-mp="2wvky47nz04pr">
              {/* Checkerboard, so transparency is visible rather than claimed. */}
              <div className="flex h-[92px] items-center justify-center rounded-xl" style={{
            backgroundColor: tone === 'white' ? '#2B211A' : '#FFFFFF',
            backgroundImage: tone === 'white' ? undefined : 'linear-gradient(45deg,#EFEAE1 25%,transparent 25%,transparent 75%,#EFEAE1 75%),linear-gradient(45deg,#EFEAE1 25%,transparent 25%,transparent 75%,#EFEAE1 75%)',
            backgroundSize: '14px 14px',
            backgroundPosition: '0 0, 7px 7px'
          }} data-id="element-9398" data-mp="2d9rptgaphvtk">
                <span ref={el => {
              frames.current[key(p.id)] = el;
            }} className="inline-flex" data-id="element-9399" data-mp="3av70djt29yhr">
                  <SurfacePill logo={p.logo} label={p.label} tone={tone} shape={shape} size={26} data-id="element-9400" data-mp="35xnjsd8w59mn" />
                </span>
              </div>
              <div className="mt-3 flex items-center gap-2" data-id="element-9401" data-mp="3fvl9l56efehd">
                <button type="button" disabled={busyNow} onClick={() => run(p.id, async svg => {
              download(await svgToPng(svg), `giga-${p.id}-${shape}-${tone}.png`);
            })} className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-[#2B211A] px-3 py-2 text-[12.5px] font-bold text-white transition-colors hover:bg-black disabled:opacity-60" data-id="element-9402" data-mp="3fx64wx0jf1ga">
                  <Download size={13} data-id="element-9403" data-mp="5jl02gvjlklb" />
                  {busyNow ? 'Preparing…' : 'PNG'}
                </button>
                <button type="button" disabled={busyNow} onClick={() => run(p.id, svg => downloadSvg(svg, `giga-${p.id}-${shape}-${tone}.svg`))} className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-[#D8CFC2] bg-white px-3 py-2 text-[12.5px] font-semibold text-[#2B211A] transition-colors hover:border-[#2B211A] disabled:opacity-60" data-id="element-9404" data-mp="9jdeha7makyl">
                  <Download size={13} data-id="element-9405" data-mp="2la7ke3dw6ika" />
                  SVG
                </button>
                <button type="button" disabled={busyNow} onClick={() => run(p.id, async svg => {
              await writeToClipboard(svg);
              setCopied(key(p.id));
              setTimeout(() => setCopied(c => c === key(p.id) ? null : c), 2000);
            })} className="inline-flex items-center justify-center rounded-lg border border-[#D8CFC2] bg-white px-2.5 py-2 text-[#2B211A] transition-colors hover:border-[#2B211A] disabled:opacity-60" aria-label={`Copy ${p.label} lockup for Figma`} data-id="element-9406" data-mp="2q7qzf5ieyzda">
                  {copiedNow ? <Check size={13} className="text-emerald-600" data-id="element-9407" data-mp="1qap9tzbhzenf" /> : <Copy size={13} data-id="element-9408" data-mp="1maf3umnpgo8k" />}
                </button>
              </div>
            </div>;
      })}
      </div>
    </section>;
}